import { Injectable, inject } from '@angular/core';
import { Actions, ofType, createEffect } from '@ngrx/effects';
import { OpportunityApprovalsService } from '@/services/opportunity-approvals.service';
import { OpportunityService } from '@/services/opportunity.service';
import * as OpportunityApprovalsActions from './opportunity-approvals.actions';
import * as OpportunityActions from '../opportunity/opportunity.actions';
import { catchError, map, mergeMap, switchMap } from 'rxjs/operators';
import { of, EMPTY } from 'rxjs';

@Injectable()
export class OpportunityApprovalsEffects {
  private actions$ = inject(Actions);
  private service = inject(OpportunityApprovalsService);
  private opportunityService = inject(OpportunityService);

  loadApprovals$ = createEffect(() =>
    this.actions$.pipe(
      ofType(OpportunityApprovalsActions.loadOpportunityApprovals),
      mergeMap((action) =>
        this.service.getApprovals(action.opportunityId).pipe(
          map((approvals) => OpportunityApprovalsActions.loadOpportunityApprovalsSuccess({ approvals })),
          catchError((error) => of(OpportunityApprovalsActions.loadOpportunityApprovalsFailure({ error })))
        )
      )
    )
  );

  createApproval$ = createEffect(() =>
    this.actions$.pipe(
      ofType(OpportunityApprovalsActions.createOpportunityApproval),
      mergeMap((action) =>
        this.service.createApproval(action.opportunityId, action.dto).pipe(
          map((approval) => OpportunityApprovalsActions.createOpportunityApprovalSuccess({ approval })),
          catchError((error) => of(OpportunityApprovalsActions.createOpportunityApprovalFailure({ error })))
        )
      )
    )
  );

  createApprovalsBatch$ = createEffect(() =>
    this.actions$.pipe(
      ofType(OpportunityApprovalsActions.createOpportunityApprovalsBatch),
      mergeMap((action) =>
        this.service.createApprovalsBatch(action.opportunityId, action.dto).pipe(
          map((approvals) => OpportunityApprovalsActions.createOpportunityApprovalsBatchSuccess({ approvals })),
          catchError((error) => of(OpportunityApprovalsActions.createOpportunityApprovalsBatchFailure({ error })))
        )
      )
    )
  );

  updateApproval$ = createEffect(() =>
    this.actions$.pipe(
      ofType(OpportunityApprovalsActions.updateOpportunityApproval),
      mergeMap((action) =>
        this.service.updateApproval(action.approvalId, action.dto).pipe(
          map((approval) => OpportunityApprovalsActions.updateOpportunityApprovalSuccess({ approval })),
          catchError((error) => of(OpportunityApprovalsActions.updateOpportunityApprovalFailure({ error })))
        )
      )
    )
  );

  deleteOpportunityApproval$ = createEffect(() =>
    this.actions$.pipe(
      ofType(OpportunityApprovalsActions.deleteOpportunityApproval),
      mergeMap(({ approval }) =>
        this.service.deleteApproval(approval.id).pipe(
          mergeMap(() => [OpportunityApprovalsActions.deleteOpportunityApprovalSuccess({ approval }), OpportunityApprovalsActions.loadOpportunityApprovals({ opportunityId: approval.opportunityId })]),
          catchError((error) => of(OpportunityApprovalsActions.deleteOpportunityApprovalFailure({ error })))
        )
      )
    )
  );

  // Workflow status update effects
  updateWorkflowStatusAfterCreateBatch$ = createEffect(() =>
    this.actions$.pipe(
      ofType(OpportunityApprovalsActions.createOpportunityApprovalsBatchSuccess),
      switchMap(({ approvals }) => {
        if (approvals.length > 0) {
          const opportunityId = approvals[0].opportunityId;
          return this.opportunityService.getOpportunityWorkflowStatus(opportunityId).pipe(
            map((workflowStatus) => OpportunityActions.updateOpportunityWorkflowStatus({ workflowStatus })),
            catchError(() => EMPTY) // Don't fail the whole flow if this fails
          );
        }
        return EMPTY;
      })
    )
  );

  updateWorkflowStatusAfterUpdate$ = createEffect(() =>
    this.actions$.pipe(
      ofType(OpportunityApprovalsActions.updateOpportunityApprovalSuccess),
      switchMap(({ approval }) => {
        const opportunityId = approval.opportunityId || approval.opportunity?.id;
        if (!opportunityId) {
          return EMPTY;
        }
        return this.opportunityService.getOpportunityWorkflowStatus(opportunityId).pipe(
          map((workflowStatus) => OpportunityActions.updateOpportunityWorkflowStatus({ workflowStatus })),
          catchError(() => EMPTY) // Don't fail the whole flow if this fails
        );
      })
    )
  );

  updateWorkflowStatusAfterDelete$ = createEffect(() =>
    this.actions$.pipe(
      ofType(OpportunityApprovalsActions.deleteOpportunityApprovalSuccess),
      switchMap(({ approval }) =>
        this.opportunityService.getOpportunityWorkflowStatus(approval.opportunityId).pipe(
          map((workflowStatus) => OpportunityActions.updateOpportunityWorkflowStatus({ workflowStatus })),
          catchError(() => EMPTY) // Don't fail the whole flow if this fails
        )
      )
    )
  );
}
